import { Side } from '@/common/constants';
import type { MarketData } from '@/common/MarketData';
import { PositionStatus, type ViewPosition } from '@/feature/terminal_tables/position/types';
import { computeLiquidationBuffer } from '@/utils/liquidationUtils';

export const liquidationAlertPositionKey = (p: Pick<ViewPosition, 'positionId' | 'marketId'>) =>
  `${p.positionId}-${p.marketId}`;

export type LiquidationAlertDecision = {
  position: ViewPosition;
  marketName: string;
  /** Share of the position's margin buffer (entry→liquidation span) the adverse move has consumed, in percent. */
  marginUsedPercent: number;
  /**
   * Remaining move to liquidation, in percent — the exact value shown in the positions table's
   * "liquidation buffer" column (see {@link computeLiquidationBuffer}), so the two never diverge.
   */
  bufferPercent: number;
  positionKey: string;
};

export function evaluateLiquidationAlerts(args: {
  positions: readonly ViewPosition[];
  markets: ReadonlyMap<number, MarketData>;
  prefs: { enabled: boolean; marginUsedPercent: number; cooldownMinutes: number };
  lastNotifiedAt: Readonly<Record<string, number>>;
  now: number;
}): LiquidationAlertDecision[] {
  const { positions, markets, prefs, lastNotifiedAt, now } = args;
  if (!prefs.enabled) return [];

  const cooldownMs = prefs.cooldownMinutes * 60_000;
  const decisions: LiquidationAlertDecision[] = [];

  for (const position of positions) {
    if (position.status !== PositionStatus.open) continue;
    // liquidationPrice === 0 is the unknown-sentinel from liquidationUtils.
    if (!(position.liquidationPrice > 0)) continue;

    const market = markets.get(position.marketId);
    if (!market || !(market.markPrice > 0)) continue;

    // The position's margin buffer in price terms: the entry→liquidation span. This equals
    // (IMR − MMR) × entryPrice and is set entirely by leverage, so using it as the
    // denominator makes the threshold a leverage-independent fraction of margin rather than
    // a raw price gap (which meant wildly different risk across leverage tiers).
    const buffer =
      position.side === Side.long
        ? position.entryPrice - position.liquidationPrice
        : position.liquidationPrice - position.entryPrice;
    if (!(buffer > 0)) continue;

    // How far the mark has moved adversely from entry toward liquidation.
    const consumed =
      position.side === Side.long ? position.entryPrice - market.markPrice : market.markPrice - position.entryPrice;
    // In profit / no adverse move — nothing to warn about.
    if (consumed <= 0) continue;

    const marginUsedPercent = (consumed / buffer) * 100;
    // At/over 100% the mark is already past liquidation — handled by position-status notifications.
    if (marginUsedPercent >= 100) continue;
    if (marginUsedPercent < prefs.marginUsedPercent) continue;

    const positionKey = liquidationAlertPositionKey(position);
    if (now - (lastNotifiedAt[positionKey] ?? 0) < cooldownMs) continue;

    // The "N% move away" figure — the same liquidation-buffer value the positions table shows,
    // sourced from the shared helper so the alert and the column stay identical.
    const bufferPercent =
      computeLiquidationBuffer({
        side: position.side,
        markPrice: market.markPrice,
        liquidationPrice: position.liquidationPrice,
      }) * 100;

    decisions.push({ position, marketName: market.name, marginUsedPercent, bufferPercent, positionKey });
  }

  return decisions;
}
